/**
 * CoiSubmit: community-of-interest intake, in the district tool's layout.
 *
 * The intake form lives in the left sidebar; the community map fills the
 * right, always visible — like MapView. Residents can submit words alone, a
 * hand-drawn polygon (freehand drawing on the shared map), or an AI-sketch
 * candidate they review and attach. "Submit community" POSTs to /v1/cois
 * (verified account required) and shows a receipt; "Save draft" keeps
 * everything in this browser (localStorage) for later. Guests may draft
 * freely; the sign-in prompt appears at submit.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { AiSketchPanel } from '../components/coi/AiSketchPanel';
import { CoiDrawMap } from '../components/coi/CoiDrawMap';
import { MyCois } from '../components/coi/MyCois';
import { MapSearch } from '../components/map/MapSearch';
import { BasemapControl } from '../components/map/BasemapControl';
import { MapReferenceLayers } from '../components/map/MapReferenceLayers';
import { ApiError } from '../lib/api';
import {
  CoiRejectedError,
  createMyCoiDraft,
  getMyCoi,
  submitCoi,
  submitMyCoiDraft,
  updateMyCoiDraft,
  type CoiReceipt,
  type CoiSketchProvenance,
} from '../lib/coi';
import { useTenantStore } from '../stores/tenant';
import { ShareCoiDialog } from '../components/coi/ShareCoiDialog';
import { ExportMenu } from '../components/ExportMenu';
import { exportCoi } from '../lib/exports';
import { coiTokenFromLink, forkCoi } from '../lib/coiSharing';
import { ShareIcon } from '../components/icons';
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '../components/icons';
import { useRequireAuth } from '../hooks/useRequireAuth';

type SubmitState = 'editing' | 'submitting' | 'receipt';

/** How the resident provides the community boundary: words alone, a
 * hand-drawn polygon, or the AI sketch assistant. */
type BoundaryMode = 'none' | 'draw' | 'sketch';

const BOUNDARY_MODES: { value: BoundaryMode; label: string; hint: string }[] = [
  { value: 'none', label: 'Text only', hint: 'Just words — no map needed.' },
  { value: 'draw', label: 'Draw it', hint: 'Draw the boundary yourself on the map.' },
  { value: 'sketch', label: 'AI sketch', hint: 'Generate a sketch from your description.' },
];

const SIDEBAR_COLLAPSE_STORAGE_KEY = 'coi:sidebar-collapsed';

const NO_BOUNDARY = {
  shapefile: 'Draw a boundary on the map first',
  equivalency: 'Draw a boundary on the map first',
} as const;

function readSidebarCollapsed(): boolean {
  try {
    const stored = window.sessionStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY);
    if (stored !== null) return stored === '1';
  } catch {
    // Storage unavailable — fall through to the viewport default.
  }
  // Narrow screens start with the sidebar collapsed so the map dominates.
  // Collapsing is a desktop affordance; phones always get the form above the map.
  return false;
}

interface DraftPayload {
  name: string;
  narrative: string;
  interests: string;
  contact: string;
  boundary: Record<string, unknown> | null;
  sketch: CoiSketchProvenance | null;
  savedAt: string;
}

function draftKey(slug: string): string {
  return `coi-draft-${slug}`;
}

function loadDraft(slug: string): DraftPayload | null {
  try {
    const raw = localStorage.getItem(draftKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DraftPayload>;
    if (typeof parsed.narrative !== 'string') return null;
    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      narrative: parsed.narrative,
      interests: typeof parsed.interests === 'string' ? parsed.interests : '',
      contact: typeof parsed.contact === 'string' ? parsed.contact : '',
      boundary:
        parsed.boundary !== null && typeof parsed.boundary === 'object'
          ? parsed.boundary
          : null,
      sketch:
        parsed.sketch !== null && typeof parsed.sketch === 'object' ? parsed.sketch : null,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
    };
  } catch {
    return null;
  }
}

export function CoiSubmit(): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  document.title = `Community of interest — ${tenant?.name ?? 'CivicLines'}`;

  const location = useLocation();
  const locationState = (location.state ?? {}) as {
    draftCoiId?: string;
    forkedFrom?: string;
    hint?: string;
  };
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [forkHint, setForkHint] = useState<string | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(false);

  const [name, setName] = useState('');
  const [narrative, setNarrative] = useState('');
  const [interests, setInterests] = useState('');
  const [contact, setContact] = useState('');
  const [boundary, setBoundary] = useState<Record<string, unknown> | null>(null);
  const [sketch, setSketch] = useState<CoiSketchProvenance | null>(null);
  const [boundaryMode, setBoundaryMode] = useState<BoundaryMode>('none');
  /** The AI-sketch candidate previewing on the map, not yet attached. */
  const [sketchPreview, setSketchPreview] = useState<Record<string, unknown> | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [map, setMap] = useState<maplibregl.Map | null>(null);
  const navigate = useNavigate();
  // Starting point: a fresh community, or a copy of someone's shared one.
  const [startMode, setStartMode] = useState<'fresh' | 'shared'>('fresh');
  const [sharedLink, setSharedLink] = useState('');
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  // Share dialog for the current community (draft or submitted).
  const [shareTarget, setShareTarget] = useState<{ id: string; title: string } | null>(null);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  // MapLibre tracks window resizes, not container resizes: tell it the
  // container changed after the sidebar collapses/expands.
  useEffect(() => {
    if (!map) return;
    const frame = requestAnimationFrame(() => map.resize());
    return () => cancelAnimationFrame(frame);
  }, [map, sidebarCollapsed]);

  function toggleSidebar(): void {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.sessionStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Session storage unavailable — the toggle still works for this view.
      }
      return next;
    });
  }

  /** Switching boundary modes discards any attached boundary and any
   * unattached sketch candidate — the modes are alternatives, not layers. */
  function handleBoundaryModeChange(mode: BoundaryMode): void {
    setBoundaryMode(mode);
    setBoundary(null);
    setSketch(null);
    setSketchPreview(null);
  }

  const [submitState, setSubmitState] = useState<SubmitState>('editing');
  const [receipt, setReceipt] = useState<CoiReceipt | null>(null);
  const { requireAuth, signInModal } = useRequireAuth();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [draftNotice, setDraftNotice] = useState<string | null>(null);

  // Forked-copy entry: load the server draft into the editor.
  useEffect(() => {
    const draftCoiId = locationState.draftCoiId;
    if (!draftCoiId || editingDraftId === draftCoiId) return;
    setLoadingDraft(true);
    setSubmitError(null);
    getMyCoi(draftCoiId)
      .then((draft) => {
        setEditingDraftId(draft.id);
        setName(draft.title ?? '');
        setNarrative(draft.narrative);
        setInterests(draft.interests ?? '');
        setContact(draft.contact ?? '');
        const geom = draft.polygon_geojson;
        setBoundary(
          geom && typeof geom.type === 'string' && Array.isArray((geom as { coordinates?: unknown }).coordinates)
            ? geom
            : null,
        );
        // Deliberately not restoring sketch_provenance into state: the stored
        // value is only replaced when the sketch panel produces a new
        // boundary this session (see handleSaveDraft).
        setForkHint(
          locationState.hint ??
            (locationState.forkedFrom
              ? `This is your editable copy of “${locationState.forkedFrom}” — adjust it, then submit.`
              : 'This is your editable copy — adjust it, then submit.'),
        );
        window.scrollTo({ top: 0 });
      })
      .catch(() => {
        setSubmitError('Could not load your copied community. Please try again.');
      })
      .finally(() => setLoadingDraft(false));
    // Only run for the navigation that carried the draft id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState.draftCoiId]);

  // Restore a saved draft once the tenant is known.
  useEffect(() => {
    if (!tenant) return;
    const draft = loadDraft(tenant.slug);
    if (!draft) return;
    setName(draft.name);
    setNarrative(draft.narrative);
    setInterests(draft.interests);
    setContact(draft.contact);
    setBoundary(draft.boundary);
    setSketch(draft.sketch);
    if (draft.savedAt) {
      setDraftNotice(
        `Restored your draft from ${new Date(draft.savedAt).toLocaleString()}.`,
      );
    }
    // Only restore once per tenant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.slug]);

  const narrativeLength = narrative.trim().length;
  const narrativeTooShort = narrativeLength > 0 && narrativeLength < 20;

  function fieldError(field: string): string | null {
    return fieldErrors[field] ?? null;
  }

  function handleSaveDraft(): void {
    if (!tenant) return;
    if (editingDraftId) {
      // Server draft (e.g. a forked copy): persist to the backend.
      setDraftNotice(null);
      updateMyCoiDraft(editingDraftId, {
        title: name.trim() || undefined,
        narrative: narrative.trim(),
        interests: interests.trim() || undefined,
        polygon_geojson: boundary,
        contact: contact.trim() || undefined,
        // Only when the sketch panel produced a new boundary this session;
        // otherwise the stored provenance (incl. forked_from) is untouched.
        ...(sketch ? { sketch_provenance: sketch } : {}),
      })
        .then(() => {
          setDraftNotice(`Draft saved ${new Date().toLocaleTimeString()} — kept in your account.`);
        })
        .catch((error: unknown) => {
          if (error instanceof CoiRejectedError) {
            const mapped: Record<string, string> = {};
            for (const e of error.fieldErrors) {
              if (!(e.field in mapped)) mapped[e.field] = e.message;
            }
            setFieldErrors(mapped);
          } else {
            setDraftNotice('Could not save the draft — please check your connection.');
          }
        });
      return;
    }
    const payload: DraftPayload = {
      name,
      narrative,
      interests,
      contact,
      boundary,
      sketch,
      savedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(draftKey(tenant.slug), JSON.stringify(payload));
      setDraftNotice(`Draft saved ${new Date().toLocaleTimeString()} — it stays in this browser.`);
    } catch {
      setDraftNotice('Could not save the draft in this browser (storage unavailable).');
    }
  }

  function clearDraft(): void {
    if (tenant) {
      try {
        localStorage.removeItem(draftKey(tenant.slug));
      } catch {
        // Best effort; the receipt is what matters.
      }
    }
  }

  /** Copy someone's shared community into the account, then edit it here. */
  async function handleCopyShared(): Promise<void> {
    const token = coiTokenFromLink(sharedLink);
    if (!token) {
      setCopyError('Paste a community share link — it looks like …/p/coi/abc123.');
      return;
    }
    setCopying(true);
    setCopyError(null);
    try {
      const draft = await forkCoi(token);
      setStartMode('fresh');
      setSharedLink('');
      navigate('/coi', {
        replace: true,
        state: {
          draftCoiId: draft.coi_id,
          forkedFrom: draft.title ?? 'a shared community',
          hint: 'Copied into your drafts — adjust the description and boundary, then submit.',
        },
      });
    } catch (error) {
      setCopyError(
        error instanceof Error && error.message
          ? error.message
          : 'That link could not be copied. Check it and try again.',
      );
    } finally {
      setCopying(false);
    }
  }

  /**
   * Share link for the community being edited. A new community lives only in
   * this browser, so it's saved to the account as a draft first.
   */
  async function handleShare(): Promise<void> {
    if (narrative.trim().length === 0) {
      setShareError('Describe your community first — the share page shows your description.');
      return;
    }
    setSharing(true);
    setShareError(null);
    const fields = {
      title: name.trim() || undefined,
      narrative: narrative.trim(),
      interests: interests.trim() || undefined,
      polygon_geojson: boundary,
      contact: contact.trim() || undefined,
      ...(sketch ? { sketch_provenance: sketch } : {}),
    };
    try {
      let id = editingDraftId;
      if (id) {
        await updateMyCoiDraft(id, fields);
      } else {
        const created = await createMyCoiDraft(fields);
        id = created.id;
        setEditingDraftId(id);
        clearDraft();
        setDraftNotice('Saved to your account as a draft so it can be shared.');
      }
      setShareTarget({ id, title: name.trim() || 'Community of interest' });
    } catch (error) {
      if (error instanceof CoiRejectedError) {
        const mapped: Record<string, string> = {};
        for (const e of error.fieldErrors) if (!(e.field in mapped)) mapped[e.field] = e.message;
        setFieldErrors(mapped);
        setShareError('Fix the highlighted fields, then share again.');
      } else {
        setShareError(error instanceof Error ? error.message : 'Could not create a share link.');
      }
    } finally {
      setSharing(false);
    }
  }

  function runExport(format: Parameters<typeof exportCoi>[0]): Promise<void> {
    return exportCoi(format, {
      title: name.trim() || 'Community of interest',
      narrative: narrative.trim(),
      interests: interests.trim() || null,
      polygon: boundary,
    });
  }

  async function handleSubmit(): Promise<void> {
    if (submitState !== 'editing') return;
    setFieldErrors({});
    setSubmitError(null);

    // Client-side guardrails mirror the backend rules (narrative >= 20 chars).
    const problems: Record<string, string> = {};
    if (narrative.trim().length < 20) {
      problems.narrative = 'Please write at least a sentence or two (20 characters minimum).';
    }
    if (Object.keys(problems).length > 0) {
      setFieldErrors(problems);
      return;
    }

    setSubmitState('submitting');
    try {
      const result = editingDraftId
        ? await submitMyCoiDraft(editingDraftId)
        : await submitCoi({
            title: name.trim() || undefined,
            narrative: narrative.trim(),
            interests: interests.trim() || undefined,
            polygon_geojson: boundary,
            sketch,
            contact: contact.trim() || undefined,
          });
      clearDraft();
      setReceipt(result);
      setSubmitState('receipt');
      setEditingDraftId(null);
      setForkHint(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error instanceof CoiRejectedError) {
        const mapped: Record<string, string> = {};
        for (const e of error.fieldErrors) {
          if (!(e.field in mapped)) mapped[e.field] = e.message;
        }
        setFieldErrors(mapped);
        if (error.fieldErrors.length === 0) {
          setSubmitError(error.message);
        }
      } else if (error instanceof ApiError && error.code === 'rate_limited') {
        setSubmitError(error.message);
      } else {
        setSubmitError(
          error instanceof Error
            ? `Could not submit: ${error.message}`
            : 'Could not submit — please check your connection and try again.',
        );
      }
      setSubmitState('editing');
    }
  }

  function handleStartOver(): void {
    setName('');
    setNarrative('');
    setInterests('');
    setContact('');
    setBoundary(null);
    setSketch(null);
    setSketchPreview(null);
    setReceipt(null);
    setFieldErrors({});
    setSubmitError(null);
    setDraftNotice(null);
    setSubmitState('editing');
  }

  if (submitState === 'receipt' && receipt) {
    return (
      <div className="mx-auto max-w-2xl py-6">
        <div className="card overflow-hidden" role="status">
          <div className="border-b border-emerald-100 bg-emerald-50 px-6 py-5">
            <p className="flex items-center gap-2 text-emerald-800">
              <CheckCircleIcon size={22} />
              <span className="text-lg font-bold">Community received — thank you.</span>
            </p>
            <p className="mt-1 text-sm text-emerald-800/80">
              Keep this receipt. Staff can look up your submission with it at any hearing.
            </p>
          </div>
          <div className="px-6 py-5">
            <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[auto,1fr]">
              <dt className="font-semibold text-stone-500">Receipt</dt>
              <dd className="break-all font-mono text-xs text-ink sm:pt-0.5">{receipt.id}</dd>
              {receipt.submitted_at && (
                <>
                  <dt className="font-semibold text-stone-500">Submitted</dt>
                  <dd className="text-ink">{new Date(receipt.submitted_at).toLocaleString()}</dd>
                </>
              )}
              <dt className="font-semibold text-stone-500">Status</dt>
              <dd className="capitalize text-ink">{receipt.status.replace(/_/g, ' ')}</dd>
            </dl>
            <h2 className="panel-title mt-6">What happens next</h2>
            <ol className="mt-2 space-y-2 text-sm leading-relaxed text-stone-700">
              {[
                `Staff review your description${boundary ? ' and map boundary' : ''} for completeness.`,
                'Accepted communities are kept together when draft districts are drawn — splitting yours takes a written justification.',
                'You can speak to it at a public hearing; bring this receipt ID so staff can pull up your submission.',
              ].map((text, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tenant-tint text-xs font-bold text-tenant-strong">
                    {i + 1}
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ol>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShareTarget({ id: receipt.id, title: name.trim() || 'Community of interest' })}
                className="btn-primary"
              >
                <ShareIcon size={16} /> Share link
              </button>
              <ExportMenu variant="secondary" onExport={runExport} unavailable={boundary ? {} : NO_BOUNDARY} />
              <button type="button" onClick={handleStartOver} className="btn-ghost">
                Submit another community
              </button>
            </div>
            {shareTarget && (
              <ShareCoiDialog
                coiId={shareTarget.id}
                coiTitle={shareTarget.title}
                onClose={() => setShareTarget(null)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  const coiDisabled = !tenant?.features.coi_enabled;

  const form = (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        requireAuth(
          () => void handleSubmit(),
          'Sign in to submit your community of interest to the jurisdiction.',
        );
      }}
      noValidate
    >
      {submitError && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {submitError}
        </p>
      )}

      <div>
        <label htmlFor="coi-name" className="field-label">
          Community name <span className="font-normal text-stone-400">(optional)</span>
        </label>
        <input
          id="coi-name"
          name="name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Elmhurst commercial corridor"
          className="field-input"
        />
      </div>

      <div>
        <label htmlFor="coi-narrative" className="field-label">
          Describe your community <span className="text-red-500">*</span>
        </label>
        <textarea
          id="coi-narrative"
          name="narrative"
          rows={5}
          required
          value={narrative}
          onChange={(event) => setNarrative(event.target.value)}
          placeholder="What ties this community together? Where are its edges?"
          aria-describedby="coi-narrative-hint"
          aria-invalid={fieldError('narrative') !== null || narrativeTooShort}
          className={`field-input ${
            fieldError('narrative') || narrativeTooShort ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''
          }`}
        />
        <p id="coi-narrative-hint" className="mt-1 text-xs text-stone-400">
          {narrativeLength < 20
            ? `${narrativeLength}/20 characters minimum — a sentence or two is enough.`
            : `${narrativeLength} characters`}
        </p>
        {(fieldError('narrative') || (narrativeTooShort && narrativeLength > 0)) && (
          <p className="mt-1 text-xs text-red-600" role="alert">
            {fieldError('narrative') ??
              'Please write at least a sentence or two (20 characters minimum).'}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="coi-interests" className="field-label">
          Shared interests or concerns <span className="font-normal text-stone-400">(optional)</span>
        </label>
        <input
          id="coi-interests"
          name="interests"
          type="text"
          value={interests}
          onChange={(event) => setInterests(event.target.value)}
          placeholder="e.g. transit access, flood mitigation, language services"
          className="field-input"
        />
      </div>

      <div>
        <label htmlFor="coi-contact" className="field-label">
          Contact <span className="font-normal text-stone-400">(optional)</span>
        </label>
        <input
          id="coi-contact"
          name="contact"
          type="text"
          value={contact}
          onChange={(event) => setContact(event.target.value)}
          placeholder="Email or phone, in case staff have a follow-up question"
          autoComplete="email"
          className="field-input"
        />
        <p className="mt-1 text-xs text-stone-400">
          Only used if staff need to clarify your submission. Never published.
        </p>
      </div>

      <div>
        <span id="coi-boundary-label" className="field-label">
          Map boundary <span className="font-normal text-stone-400">(optional)</span>
        </span>
        <div
          className="mt-2 grid grid-cols-3 gap-0.5 rounded-xl bg-stone-100 p-1"
          role="radiogroup"
          aria-labelledby="coi-boundary-label"
        >
          {BOUNDARY_MODES.map((mode) => (
            <button
              key={mode.value}
              type="button"
              role="radio"
              aria-checked={boundaryMode === mode.value}
              title={mode.hint}
              onClick={() => handleBoundaryModeChange(mode.value)}
              className={`rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${
                boundaryMode === mode.value
                  ? 'bg-white text-ink shadow-sm ring-1 ring-stone-200'
                  : 'text-stone-500 hover:text-ink'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-stone-400">
          {BOUNDARY_MODES.find((mode) => mode.value === boundaryMode)?.hint}{' '}
          The written description above is always sufficient.
        </p>
      </div>

      {boundaryMode === 'draw' && (
        <p className="rounded-lg bg-tenant-soft px-3 py-2 text-xs leading-relaxed text-tenant-strong">
          Use the map — click points around your community (or switch to{' '}
          <span className="font-semibold">Trace</span> and drag), then click the first point or
          press <span className="font-semibold">Enter</span> to close the shape. You can drag points
          afterwards to fine-tune it.
        </p>
      )}
      {boundaryMode === 'sketch' && (
        <AiSketchPanel
          narrative={narrative}
          accepted={boundary !== null}
          onPreview={setSketchPreview}
          onAccept={(polygon, provenance) => {
            setBoundary(polygon);
            setSketch(provenance);
          }}
        />
      )}
      {boundary && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">
          Map boundary attached — it will be submitted with your description.
        </p>
      )}
      {fieldError('polygon_geojson') && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
          {fieldError('polygon_geojson')}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-stone-100 pt-4">
        <button
          type="submit"
          disabled={submitState === 'submitting' || coiDisabled}
          className="btn-primary"
        >
          {submitState === 'submitting' ? 'Submitting…' : 'Submit community'}
        </button>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={coiDisabled}
          className="btn-secondary"
          title="Keep your progress in this browser to finish later"
        >
          Save draft
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() =>
            requireAuth(() => void handleShare(), 'Sign in to get a share link for your community.')
          }
          disabled={sharing || coiDisabled}
          className="btn-ghost"
          title="Get a public link others can view, co-sign, or copy"
        >
          <ShareIcon size={16} />
          {sharing ? 'Preparing link…' : 'Share link'}
        </button>
        <ExportMenu
          className="inline-block"
          onExport={runExport}
          unavailable={boundary ? {} : NO_BOUNDARY}
        />
      </div>
      {shareError && (
        <p className="text-xs text-red-600" role="alert">
          {shareError}
        </p>
      )}
      {shareTarget && (
        <ShareCoiDialog
          coiId={shareTarget.id}
          coiTitle={shareTarget.title}
          onClose={() => setShareTarget(null)}
        />
      )}
      {coiDisabled && (
        <p className="text-xs text-amber-700">
          COI collection is not enabled for this jurisdiction yet.
        </p>
      )}
    </form>
  );

  return (
    <div className="flex flex-col gap-3">
      {signInModal}
      {/* Desktop: form column + map. Phones/tablets: map on top, the form
          below it in its own scrolling area. */}
      <div className="flex h-[calc(100dvh-5.5rem)] min-h-[520px] flex-col gap-3 lg:min-h-[600px]">
      {forkHint && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" role="status">
          {forkHint}
        </p>
      )}
      {loadingDraft && (
        <p className="text-sm text-stone-500">Loading your copy…</p>
      )}
      {draftNotice && (
        <p className="rounded-lg bg-tenant-soft px-3 py-2 text-xs text-tenant-strong" role="status">
          {draftNotice}
        </p>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        {sidebarCollapsed && (
          <div className="hidden w-10 shrink-0 items-start justify-center lg:flex">
            <button
              type="button"
              onClick={toggleSidebar}
              aria-expanded={false}
              aria-label="Show community form"
              title="Show community form"
              className="card flex flex-col items-center gap-2 px-1.5 py-3 text-stone-500 hover:text-ink"
            >
              <ChevronRightIcon size={16} />
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] [writing-mode:vertical-rl]">
                Form
              </span>
            </button>
          </div>
        )}
          <aside
            className={`card order-2 min-h-0 w-full flex-1 overflow-y-auto overscroll-contain p-5 lg:order-none lg:w-[23rem] lg:flex-none lg:shrink-0 ${
              sidebarCollapsed ? 'lg:hidden' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="panel-title text-[color:var(--tenant-primary)]">Community of interest</p>
                <h1 className="mt-1 text-xl font-extrabold leading-tight tracking-tight text-ink">
                  Tell us about your community
                </h1>
              </div>
              <button
                type="button"
                onClick={toggleSidebar}
                aria-expanded={true}
                aria-label="Hide community form"
                title="Hide the form — the map takes the page"
                className="btn-ghost hidden h-7 w-7 shrink-0 p-0 lg:inline-flex"
              >
                <ChevronLeftIcon size={16} />
              </button>
            </div>
            <p className="mb-5 mt-2 text-sm leading-relaxed text-stone-600">
              A neighborhood or group with shared concerns that should stay together when{' '}
              {tenant?.name ?? 'the jurisdiction'} draws districts. No map skills needed — start
              with words.
            </p>
            <div className="mb-5">
              <span className="field-label">Start from</span>
              <div className="mt-1.5 grid grid-cols-2 gap-0.5 rounded-xl bg-stone-100 p-1" role="radiogroup" aria-label="Start from">
                {(
                  [
                    ['fresh', 'Start fresh'],
                    ['shared', 'Someone’s shared community'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={startMode === value}
                    onClick={() => {
                      setStartMode(value);
                      setCopyError(null);
                    }}
                    className={`rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${
                      startMode === value
                        ? 'bg-white text-ink shadow-sm ring-1 ring-stone-200'
                        : 'text-stone-500 hover:text-ink'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {startMode === 'shared' && (
                <div className="mt-3 rounded-xl border border-stone-200 bg-stone-50/60 p-3">
                  <label className="field-label">
                    Share link
                    <input
                      type="url"
                      value={sharedLink}
                      onChange={(e) => setSharedLink(e.target.value)}
                      placeholder="https://…/p/coi/abc123"
                      className="field-input"
                    />
                  </label>
                  <p className="mt-1.5 text-[11px] leading-snug text-stone-500">
                    We’ll copy it into your account as a draft you can edit — the original never
                    changes. Requires a free account.
                  </p>
                  <button
                    type="button"
                    disabled={copying || sharedLink.trim().length === 0}
                    onClick={() =>
                      requireAuth(
                        () => void handleCopyShared(),
                        'Sign in to copy a shared community into your drafts.',
                      )
                    }
                    className="btn-primary btn-sm mt-2.5"
                  >
                    {copying ? 'Copying…' : 'Copy and edit'}
                  </button>
                  {copyError && (
                    <p className="mt-2 text-xs text-red-600" role="alert">
                      {copyError}
                    </p>
                  )}
                </div>
              )}
            </div>
            {form}
          </aside>


        <div
          className={`card relative order-1 shrink-0 overflow-hidden lg:order-none lg:h-auto lg:min-h-0 lg:flex-1 lg:shrink ${
            boundaryMode === 'draw' ? 'h-[56dvh]' : 'h-[40dvh]'
          }`}
        >
          {tenant ? (
            <>
              <CoiDrawMap
                tenant={tenant}
                drawEnabled={boundaryMode === 'draw'}
                accepted={boundary}
                preview={boundary ? null : sketchPreview}
                onAccept={(polygon) => {
                  setBoundary(polygon);
                  setSketch(null);
                }}
                onClear={() => {
                  setBoundary(null);
                  setSketch(null);
                }}
                onRequestDraw={() => handleBoundaryModeChange('draw')}
                onMapReady={setMap}
              />
              {map && (
                <MapSearch
                  map={map}
                  className="absolute left-3 top-3 z-20 w-[calc(100%-12.5rem)] max-w-[18rem] lg:w-72"
                />
              )}
              <div className="absolute right-3 top-3 z-30 flex items-start gap-2">
                <BasemapControl />
                {map && (
                  <MapReferenceLayers
                    map={map}
                    tenant={tenant}
                    beforeId="coi-accepted-fill"
                    context="coi"
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-stone-500">
              Loading map…
            </div>
          )}
        </div>
      </div>
      </div>

      <MyCois />
    </div>
  );
}
